import React, { useState } from 'react';
import SectionBasePage from 'components/SectionBasePage';
import { useDispatch, useSelector } from 'hooks/store';
import { Tabs } from 'UI';
import { Form } from 'antd';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';

import { unwrapResult } from '@reduxjs/toolkit';
import { InventoryEntryTypeEnum, InventoryTypeEnum, MenuEntryType } from 'constants/inventory';
import { CreateOrganizationTrayInputData, CreateOrganizationTrayOutputData } from 'api/organization';
import { createOrganizationTrayInputAction, createOrganizationTrayOutputAction, createOrganizationTrayTransferAction } from 'state/slices/organizations';
import { useParams } from 'react-router';
import { userPermissionsSelector } from 'state/slices/session';
import { createClientTrayTransferAction } from 'state/slices/clients';

import EntryInputModal, { EntryInputFormData } from './components/EntryInputModal';
import EntryOutputModal, { EntryOutputFormData } from './components/EntryOutputModal';
import EntryTransferModal, { EntryTransferFormData } from './components/EntryTransferModal';
import Extra from './components/Extra';
import InventoryView from './components/InventoryView';
import EntriesView from './components/EntriesView';

const getFormQuantityAndPrice = (
  formQuantity: string,
  formPrice: string,
): [number, number] => {
  const parsedFormPrice = parseFloat(formPrice);
  const parsedFormQuantity = parseFloat(formQuantity);
  const price = Math.round((parsedFormPrice * 100));
  return [parsedFormQuantity, price];
};

enum TabKeys {
  INVENTORY = 'INVENTORY',
  ENTRIES = 'ENTRIES',
}

interface TrayInventoryRouteParams {
  organizationId: string
}

const OrganizationTrayPage = (): JSX.Element => {
  const dispatch = useDispatch();

  const {
    organizationId: activeOrganizationId,
  } = useParams<TrayInventoryRouteParams>();
  const userPermissions = useSelector(userPermissionsSelector) || [];
  const organizations = userPermissions?.map((perm) => perm.organization);
  const activeOrganization = organizations.find((org) => org.id === activeOrganizationId) || {
    id: 'invalidOrgId',
    name: '',
  };

  const [entryInputForm] = Form.useForm<EntryInputFormData>();
  const [entryOutputForm] = Form.useForm<EntryOutputFormData>();
  const [entryTransferForm] = Form.useForm<EntryTransferFormData>();

  const [isExtraPopoverVisible, setExtraPopoverVisible] = useState(false);
  const [isEntryInputModalOpen, setEntryInputModalOpen] = useState(false);
  const [isEntryOutputModalOpen, setEntryOutputModalOpen] = useState(false);
  const [isEntryTransferModalOpen, setEntryTransferModalOpen] = useState(false);

  const [isLoading, setLoading] = useState(false);
  const [shouldReload, setReload] = useState(false);

  const openEntryInputModal = () => {
    setEntryInputModalOpen(true);
  };

  const openEntryOutputModal = () => {
    setEntryOutputModalOpen(true);
  };

  const openEntryTransferModal = () => {
    setEntryTransferModalOpen(true);
  };

  const openEntryModal = (entryType?: MenuEntryType) => {
    setExtraPopoverVisible(false);
    const entryModalMapping: Record<MenuEntryType, () => void> = {
      [InventoryEntryTypeEnum.INPUT]: openEntryInputModal,
      [InventoryEntryTypeEnum.OUTPUT]: openEntryOutputModal,
      [InventoryEntryTypeEnum.TRANSFER]: openEntryTransferModal,
    };

    if (entryType) {
      entryModalMapping[entryType]();
    }
  };

  const closeEntryInputModal = () => {
    setEntryInputModalOpen(false);
    entryInputForm.resetFields();
  };

  const closeEntryOutputModal = () => {
    setEntryOutputModalOpen(false);
    entryOutputForm.resetFields();
  };

  const closeEntryTransferModal = () => {
    setEntryTransferModalOpen(false);
    entryTransferForm.resetFields();
  };

  const submitEntryInputForm = async () => {
    try {
      await entryInputForm.validateFields();
    } catch (e) {
      const errorAlert = createAlertNotification(
        'Verifique os campos com erro.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      return;
    }

    const formData: EntryInputFormData = entryInputForm.getFieldsValue();

    const parsedItems = formData.items.map((entryItem) => {
      const [quantity, price] = getFormQuantityAndPrice(
        entryItem.quantity,
        entryItem.totalPrice,
      );
      return {
        trayId: entryItem.tray,
        quantity,
        price,
      };
    });

    const entryInputData: CreateOrganizationTrayInputData = {
      organizationId: activeOrganizationId,
      data: {
        entryDate: formData.entryDate.toISOString(),
        fiscalNote: formData.fiscalNote,
        deliveredBy: formData.deliveredBy,
        receivedBy: formData.receivedBy,
        notes: formData.notes,
        items: parsedItems,
      },
    };

    try {
      setLoading(true);
      const result = await dispatch(createOrganizationTrayInputAction(entryInputData));
      unwrapResult(result);
      const successAlert = createAlertNotification(`
      Entrada de insumo criada com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      setLoading(false);
      closeEntryInputModal();
      setReload(true);
    } catch (err) {
      const errorAlert = createAlertNotification(
        'Erro interno ao tentar criar entrada de insumo. Tente novamente.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  const submitEntryOutputForm = async () => {
    try {
      await entryOutputForm.validateFields();
    } catch (e) {
      const errorAlert = createAlertNotification(
        'Verifique os campos com erro.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      return;
    }

    const formData: EntryOutputFormData = entryOutputForm.getFieldsValue();

    const parsedItems = formData.items.map((entryItem) => {
      const parsedFormQuantity = parseFloat(entryItem.quantity);
      return {
        trayId: entryItem.tray,
        quantity: parsedFormQuantity,
      };
    });

    const entryOutputData: CreateOrganizationTrayOutputData = {
      organizationId: activeOrganizationId,
      data: {
        entryDate: formData.entryDate.toISOString(),
        costCenter: formData.costCenter,
        outputType: formData.outputType,
        notes: formData.notes,
        items: parsedItems,
      },
    };

    try {
      setLoading(true);
      const result = await dispatch(createOrganizationTrayOutputAction(entryOutputData));
      unwrapResult(result);
      const successAlert = createAlertNotification(`
      Saída de insumo criada com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      setLoading(false);
      closeEntryOutputModal();
      setReload(true);
    } catch (err) {
      // TODO: check error type to see if we should display a specific message
      // Ex: insufficient quantity to withdraw
      const errorAlert = createAlertNotification(
        'Erro interno ao tentar criar saída de insumo. Tente novamente.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  const submitEntryTransferForm = async () => {
    try {
      await entryTransferForm.validateFields();
    } catch (e) {
      const errorAlert = createAlertNotification(
        'Verifique os campos com erro.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      return;
    }

    const formData: EntryTransferFormData = entryTransferForm.getFieldsValue();

    const parsedItems = formData.items.map((entryItem) => {
      const [quantity, price] = getFormQuantityAndPrice(
        entryItem.quantity,
        entryItem.totalPrice,
      );
      return {
        trayId: entryItem.tray,
        quantity,
        price,
      };
    });

    const data = {
      entryDate: formData.entryDate.toISOString(),
      source: activeOrganizationId,
      sourceType: InventoryTypeEnum.ORGANIZATION,
      notes: formData.notes,
      items: parsedItems,
    };

    const orgAction = createOrganizationTrayTransferAction({
      organizationId: formData.destinationOrganization,
      data,
    });

    const clientAction = createClientTrayTransferAction({
      clientId: formData.destinationClient,
      data,
    });

    try {
      setLoading(true);
      let result;
      if (formData.destinationType === InventoryTypeEnum.ORGANIZATION) {
        result = await dispatch(orgAction);
      } else {
        result = await dispatch(clientAction);
      }
      unwrapResult(result);
      const successAlert = createAlertNotification(`
      Insumo transferido com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      setLoading(false);
      closeEntryTransferModal();
      setReload(true);
    } catch (err) {
      // TODO: check error type to see if we should display a specific message
      // Ex: insufficient quantity to withdraw
      const errorAlert = createAlertNotification(
        'Erro interno ao tentar transferir insumo. Tente novamente.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  const onReloadFinish = () => {
    setReload(false);
  };

  return (
    <SectionBasePage>
      <>
        <Tabs
          tabBarExtraContent={(
            <Extra
              onVisibleChange={(isVisible) => setExtraPopoverVisible(isVisible)}
              onEntryClick={(type?: MenuEntryType) => openEntryModal(type)}
              isPopoverVisible={isExtraPopoverVisible}
            />
          )}
        >
          <Tabs.TabPane tab="Estoque atual" key={TabKeys.INVENTORY}>
            <InventoryView
              shouldReload={shouldReload}
              onReloadFinish={onReloadFinish}
              activeOrganization={activeOrganization}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Transações" key={TabKeys.ENTRIES}>
            <EntriesView shouldReload={shouldReload} onReloadFinish={onReloadFinish} />
          </Tabs.TabPane>
        </Tabs>
        <EntryInputModal
          formHandler={entryInputForm}
          onSubmit={submitEntryInputForm}
          onCancel={closeEntryInputModal}
          isLoading={isLoading}
          isVisible={isEntryInputModalOpen}
          activeOrganization={activeOrganization}
        />
        <EntryOutputModal
          formHandler={entryOutputForm}
          onSubmit={submitEntryOutputForm}
          onCancel={closeEntryOutputModal}
          isLoading={isLoading}
          isVisible={isEntryOutputModalOpen}
          activeOrganization={activeOrganization}
        />
        <EntryTransferModal
          formHandler={entryTransferForm}
          onSubmit={submitEntryTransferForm}
          onCancel={closeEntryTransferModal}
          isLoading={isLoading}
          isVisible={isEntryTransferModalOpen}
          activeOrganization={activeOrganization}
        />
      </>
    </SectionBasePage>
  );
};

export default OrganizationTrayPage;
